import { useCallback } from 'react';
import { Job, ReportTemplate } from '@ais-ui/websockets';
import { useReportContext } from './ReportContext';
import { useMapPanelContext } from '../Map/MapPanel/MapPanelContext';
import { CENTERCOMPONENTS } from '../Map/MapPanel/CenterComponents';

/**
 * Hook that provides actions for showing/hiding reports.
 * 
 * This hook bridges ReportContext and MapPanelContext to provide
 * a clean API for controlling report visibility. It handles all
 * the state updates needed across both contexts.
 * 
 * Usage:
 * ```tsx
 * const { showReport, hideReport, isReportVisible } = useReportActions();
 * 
 * // Show a report
 * showReport(job, template);
 * 
 * // Hide the current report
 * hideReport();
 * ```
 */
export const useReportActions = () => {
  const {
    selectedJob,
    setSelectedJob,
    setSelectedReportTemplate,
    setReportTableVisible,
    setLegend,
    setIsWaiting,
    setHasLoaded,
    setFilterByMapView,
    setShowTracks,
    setReportDetails,
    reportTableVisible,
  } = useReportContext();

  const {
    setCenterComponent,
    setCenterComponentData,
    setTableExpanded,
  } = useMapPanelContext();

  /**
   * Show a report - sets up all necessary state for displaying a report
   */
  const showReport = useCallback((job: Job, template: ReportTemplate) => {
    // Set the report selection
    setSelectedJob(job);
    setSelectedReportTemplate(template);
    
    // Mark report table as visible (feature-owned state)
    setReportTableVisible(true);
    
    // Ensure table panel is expanded
    setTableExpanded(true);
  }, [
    setSelectedJob,
    setSelectedReportTemplate,
    setReportTableVisible,
    setTableExpanded,
  ]);

  /**
   * Hide the current report - cleans up all report-related state
   */
  const hideReport = useCallback(() => {
    // Clear report selection
    setSelectedJob(null);
    setSelectedReportTemplate(null);
    setReportDetails(null);
    
    // Mark report table as hidden (feature-owned state)
    setReportTableVisible(false);
    
    // Clear map panel state for reports
    setCenterComponent(CENTERCOMPONENTS.NONE);
    setCenterComponentData(null);
    
    // Reset other report-specific state
    setLegend(null);
    setIsWaiting(false);
    setHasLoaded(false);
    setFilterByMapView(false);
    setShowTracks(false);
  }, [
    setSelectedJob,
    setSelectedReportTemplate,
    setReportDetails,
    setReportTableVisible,
    setCenterComponent,
    setCenterComponentData,
    setLegend,
    setIsWaiting,
    setHasLoaded,
    setFilterByMapView,
    setShowTracks,
  ]);

  /**
   * Check if a specific report is currently visible
   */
  const isReportVisible = useCallback((jobUuid: string) => {
    return selectedJob?.uuid === jobUuid && reportTableVisible;
  }, [selectedJob, reportTableVisible]);

  return {
    showReport,
    hideReport,
    isReportVisible,
    /** Whether any report is currently visible */
    hasVisibleReport: selectedJob !== null && reportTableVisible,
  };
};
